import {
  Button,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
} from "@material-ui/core";
import React, { FunctionComponent, useEffect, useState } from "react";
import { LookupPicker } from "@bg/shared/lib/components/pickers";
import { gridContainerSpacing } from "@bg/shared/lib/theme/themes";
import { NewDocument } from "@bg/shared/lib/dal/models";
import { ChangeEvent } from "react";
import { Cancel } from "@material-ui/icons";
import { colors } from "@bg/shared/lib/theme";
import SummaryPatientHeader from "../OrderHistory/OrderTable/SummaryPatientHeader";

const useStyles = makeStyles({
  buttonContainer: {
    paddingTop: ".75em",
    paddingBottom: ".75em",
  },
  floatRight: {
    float: "right",
  },
  redColor: {
    color: colors.candyRed,
  },
  selectButton: {
    textTransform: "none",
    color: colors.white,
    backgroundColor: colors.mediumBrown,
    "&:hover": {
      background: darken(colors.mediumBrown, 0.1),
    },
  },
  uploadButton: {
    textTransform: "none",
    color: colors.white,
    backgroundColor: colors.powderBlue,
    "&:hover": {
      background: darken(colors.powderBlue, 0.1),
    },
  },
  inputFileField: {
    display: "none",
  },
});

interface UploadDocumentDialogProps {
  openDialog: boolean;
  orderInfo?: SummaryPatientHeader;
  closeDialog: () => void;
  uploadFile: (document: NewDocument, file: File) => void;
}

const UploadDocumentDialog: FunctionComponent<UploadDocumentDialogProps> = ({
  openDialog,
  orderInfo,
  closeDialog,
  uploadFile,
}) => {
  const [file, setFile] = useState(undefined as File | undefined);
  const fileRef = React.createRef<HTMLInputElement>();
  const [doc, setDoc] = useState(new NewDocument());
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [fileExceedsMaxSize, setFileExceedsMaxSize] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    if (!openDialog) {
      setFile(undefined);
      setDoc(new NewDocument());
    }
  }, [openDialog]);

  const handleDocumentInfoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newDoc = Object.assign({}, doc) as any;
    newDoc[event.target.name] = event.target.value;
    setDoc(newDoc);
  };

  const handleFileUpload = async () => {
    if (doc && file) {
      uploadFile(doc, file);
    }
  };

  const handleDocumentTypeChange = (
    event: ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const newDoc = Object.assign({}, doc);
    newDoc.typeId = parseInt(event.target.value as string, 10);
    setDoc(newDoc);
  };

  const handleSelectFileClick = () => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    var file = event.target.files && event.target.files[0];
    setFile(file || undefined);
    const newDoc = Object.assign({}, doc);
    if (file) {
      if (file.size > 20000000) {
        setFileExceedsMaxSize(true);
      } else {
        setFileExceedsMaxSize(false);
      }
      newDoc.title = file.name;
      setDoc(newDoc);
      setIsFileUploaded(true);
    }
  };

  const isUploadButtonDisabled = () =>
    !(doc.title && doc.typeId && isFileUploaded && !fileExceedsMaxSize);

  const message = (doc: NewDocument, file?: File) => {
    if (fileExceedsMaxSize) {
      return "File limit has been exceeded. Maximum size is 20MB.";
    }
    return !doc.typeId
      ? "Please select a document type."
      : !(file && file.name)
      ? "Please select a file (20MB max)."
      : file.name;
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={openDialog}>
      <div className={classes.buttonContainer}>
        <Button className={classes.floatRight} onClick={closeDialog}>
          <Cancel className={classes.redColor} fontSize="medium" />
        </Button>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          {orderInfo && (
            <Grid container spacing={gridContainerSpacing}>
              {(orderInfo.firstName || orderInfo.lastName) && (
                <Grid item xs={3}>
                  <b>Name: </b>
                  <span>
                    {orderInfo &&
                      `${orderInfo.firstName} ${orderInfo.lastName}`}
                  </span>
                </Grid>
              )}
              {orderInfo.dobStr && (
                <Grid item xs={2}>
                  <b>DOB: </b>
                  <span>{orderInfo.dobStr}</span>
                </Grid>
              )}
              {!!orderInfo.labNumber && (
                <Grid item xs={2}>
                  <b>Lab Number: </b>
                  <span>{orderInfo && orderInfo.labNumber}</span>
                </Grid>
              )}
              {orderInfo.testName && (
                <Grid item xs={5}>
                  <b>Test Name: </b>
                  <span>{orderInfo && orderInfo.testName}</span>
                </Grid>
              )}
            </Grid>
          )}

          <Grid container spacing={gridContainerSpacing}>
            <Grid item xs={12} md={4}>
              <LookupPicker
                type="Document Type"
                fullWidth
                id="document-type"
                name="typeId"
                value={doc.typeId}
                onChange={handleDocumentTypeChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label="Description"
                id="document-description"
                fullWidth
                name="description"
                value={doc.description}
                onChange={handleDocumentInfoChange}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={8}>
            <input
              id="document-file"
              type="file"
              className={classes.inputFileField}
              onChange={handleFileChange}
              ref={fileRef}
            />
            <div>
              <i>{message(doc, file)}</i>
            </div>
          </Grid>
        </DialogContent>
      </div>
      <DialogActions style={{ marginRight: "16px" }}>
        <Button
          onClick={handleSelectFileClick}
          variant="contained"
          className={classes.selectButton}
          disabled={!doc.typeId}
        >
          Select File
        </Button>
        <Button
          onClick={handleFileUpload}
          className={classes.uploadButton}
          variant="contained"
          disabled={isUploadButtonDisabled()}
        >
          Upload File
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentDialog;
